import { useState, useMemo, useEffect } from "react";
import { formatValue } from "../data/data";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import { getInvoices, isAuthError, forceLogout } from "../service/rpc";
import { useUser } from "../context/UserContext";
import "../styles/PageShared.css";

const ITEMS_PER_PAGE = 6;

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [viewType, setViewType] = useState("list");
  const { uid, loading: userLoading } = useUser();

  //logic of kanban view in mobile 

  useEffect(() => {
    if (window.innerWidth <= 992) {
      setViewType("grid");
    }
  }, [])
  
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;

        const { invoices: data, count } = await getInvoices([], ITEMS_PER_PAGE, offset);
        const mappedData = data.map(inv => {
          let paymentStatus = 'Unknown';
          let badgeClass = 'draft';

          const invState = inv.state;
          if (invState === 'paid') {
            paymentStatus = 'Paid';
            badgeClass = 'done';
          } else if (invState === 'confirmed') {
            paymentStatus = 'Confirmed';
            badgeClass = 'pending';
          } else if (invState === 'cancelled') {
            paymentStatus = 'Cancelled';
            badgeClass = 'cancelled';
          } else if (invState === 'draft') {
            paymentStatus = 'Draft';
            badgeClass = 'draft';
          }

          return {
            id: inv.name,
            displayId: inv.id,
            contract: inv.partner_id ? inv.partner_id[1] : "General",
            client: inv.partner_id ? inv.partner_id[1] : "Unknown",
            amount: inv.amount_total || 0,
            date: inv.invoice_date && typeof inv.invoice_date === 'string' ? inv.invoice_date.split(' ')[0] : (inv.invoice_date || "N/A"),
            due: inv.date_due && typeof inv.date_due === 'string' ? inv.date_due.split(' ')[0] : (inv.date_due || "N/A"),
            status: paymentStatus,
            badgeClass: badgeClass,
            accessToken: inv.access_token
          };
        });
        setInvoices(mappedData);
        setTotalItems(count);
      } catch (err) {
        console.error("Failed to fetch invoices:", err);
        if (isAuthError(err)) { forceLogout(); return; }
        setError(err.isServerDown
          ? "Server is temporarily unavailable. Please try again later."
          : "Error loading invoices. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    }
    if (!userLoading) {
      fetchData();
    }
  }, [search, currentPage, uid, userLoading]);

  const pagedData = invoices;

  const handleDownload = (inv) => {
    const reportUrl = `http://207.180.242.15:1869/my/invoices/${inv.displayId}?access_token=${inv.accessToken}&report_type=pdf&download=true`;
    window.location.href = reportUrl;
  };

  if (error) {
    return (
      <main className="main">
        <div className="error-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>
            {error.includes('unavailable') ? '🔌' : '⚠️'}
          </div>
          <h2 style={{ color: '#1e293b', marginBottom: '10px', fontFamily: 'Sora' }}>
            {error.includes('unavailable') ? 'Server Unavailable' : 'Something went wrong'}
          </h2>
          <p style={{ color: '#64748b', marginBottom: '20px' }}>{error}</p>
          <button className="btn-create" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </main>
    );
  }

  return (
    <main className="main">
      <div className="page-header">
        <h1 className="page-header__title">Invoices</h1>
        {/* <div className="page-header__actions">
          <button className="btn-create">New Invoice</button>
        </div> */}
      </div>

      <div className="task-tab-header">
        <SearchBar
          search={search}
          setSearch={setSearch}
          placeholder="Search by ID, contract, or client…"
          style={{ flex: 1, margin: 0 }}
        />
        <div className="view-toggle">
          <button
            className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`}
            onClick={() => setViewType('list')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            List
          </button>
          <button
            className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`}
            onClick={() => setViewType('grid')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
            Kanban
          </button>
        </div>
      </div>

      <div className={`table-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading...</p>
          </div>
        )}
        {viewType === "list" ? (
          <table className="table">
            <thead>
              <tr>
                <th className="table__th">Date</th>
                <th className="table__th">Invoice</th>
                <th className="table__th">Amount</th>
                <th className="table__th">Status</th>
                <th className="table__th">Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedData.length === 0 ? (
                <tr><td className="table__empty" colSpan={6}>No paid invoices found.</td></tr>
              ) : pagedData.map((inv) => (
                <tr key={inv.id} className="table__row">
                  <td className="table__td"><span className="table__client">{inv.date}</span></td>
                  <td className="table__td"><span className="table__name">{inv.id}</span></td>
                  <td className="table__td"><span className="table__value">{formatValue(inv.amount)}</span></td>
                  <td className="table__td">
                    <span className={`badge badge--${inv.badgeClass}`}>{inv.status}</span>
                  </td>
                  <td className="table__td">
                    <button className="btn-download" onClick={() => handleDownload(inv)} title="Download PDF">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="task-grid">
            {pagedData.length === 0 ? (
              <div className="table__empty" style={{ padding: '40px' }}>No paid invoices found.</div>
            ) : pagedData.map((inv) => (
              <div key={inv.id} className="kanban-card">
                <div className="kanban-card__top">
                  <span className={`badge badge--${inv.badgeClass}`}>{inv.status}</span>
                  <span className="kanban-card__id">{inv.id}</span>
                </div>
                <div style={{ marginBottom: '16px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                  Issued: {inv.date}
                </div>

                <div className="kanban-card__bottom">
                  <div style={{ fontWeight: '700', color: '#10b981' }}>{formatValue(inv.amount)}</div>
                  <button className="btn-download btn-download--mini" onClick={() => handleDownload(inv)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>
    </main>
  );
}
